/**
 * The wire contract between client and server.
 *
 * Declared before any implementation so the server, the client and the lobby
 * can be built against one another without drifting — the same discipline
 * src/physics/surface.ts and src/rl/types.ts established.
 *
 * ===========================================================================
 * WHY SERVER-AUTHORITATIVE WITH CLIENT PREDICTION
 * ===========================================================================
 *
 * The simulation is bit-deterministic, headless, fully serializable and runs at
 * ~75,000 ticks/sec single-threaded. That combination is exactly what this
 * architecture needs and it is rare enough to be worth stating:
 *
 *   - The server can run the authoritative `World` for many matches on one box.
 *   - A client can run the SAME `World` locally, ahead of the server, and get
 *     bit-identical results for the same inputs — so prediction is exact rather
 *     than approximate, and reconciliation only has to correct for inputs the
 *     client had not yet seen (other players), not for float drift.
 *   - Rewind-and-replay is `deserializeState` plus N cheap ticks.
 *
 * The alternative — lockstep — would be cheaper on bandwidth but stalls every
 * player on the slowest connection, which is unplayable for a game where a
 * 30 ms swing decides a 50/50.
 *
 * ===========================================================================
 * WHY WEBSOCKET FIRST, BEHIND AN INTERFACE
 * ===========================================================================
 *
 * WebSocket is TCP: a dropped packet head-of-line blocks everything behind it,
 * which is the wrong trade for a twitch game. WebRTC DataChannel in unreliable
 * /unordered mode is the right transport, but it needs signalling, STUN and
 * usually TURN, and that is a lot of moving parts to debug at the same time as
 * the netcode itself.
 *
 * So: WebSocket now, `Transport` below as the seam, and nothing above this file
 * knows which one it is talking to. Snapshots are already self-contained and
 * idempotent (see `Snapshot.baseTick`), which is what makes an unreliable
 * transport a drop-in rather than a rewrite.
 *
 * ===========================================================================
 * WHY BINARY, NOT JSON
 * ===========================================================================
 *
 * `serializeState` produces several kB of JSON for a 3v3. At 30 Hz to 6 clients
 * that is megabytes per second per match, which is a hosting bill rather than a
 * technical limit. Quantized binary puts a full snapshot near 200 bytes.
 *
 * Quantization is chosen against the arena, not guessed:
 *   - position  int16 over [-8192, 8192]  -> 0.25 uu, a quarter of a ball radius
 *   - velocity  int16 over [-6000, 6000]  -> 0.18 uu/s against a 2300 uu/s car
 *   - orientation  smallest-three quaternion, 3x10 bits + 2 bits index
 *   - booleans packed into one bitfield per car
 *
 * The client PREDICTS from its own exact simulation, so quantization error only
 * ever affects the correction term, never the car the player is driving.
 */

import type { CarInput } from '../physics/car-state';
import type { MutatorConfig } from '../physics/mutators';
import type { CarBody } from '../physics/car-config';

// ---------------------------------------------------------------------------
// Versioning
// ---------------------------------------------------------------------------

/**
 * Bumped on any wire-format change. A client and server that disagree must
 * refuse to play rather than desync silently — a mismatched snapshot decodes
 * into plausible garbage, and the failure looks like "the physics is broken".
 */
export const PROTOCOL_VERSION = 2;

// ---------------------------------------------------------------------------
// Modes
// ---------------------------------------------------------------------------

export type MultiplayerMode =
  /** Ranked-style 1v1/2v2/3v3 from a queue. */
  | 'duel'
  | 'doubles'
  | 'standard'
  /** Private room joined with a code. */
  | 'private'
  /** Everyone on one team against bots. */
  | 'coop'
  /** Free-for-all knockabout, no teams. */
  | 'freeplay';

export interface MatchModeSpec {
  readonly mode: MultiplayerMode;
  /** Players per team. `freeplay` ignores teams. */
  readonly teamSize: number;
  /** Fill empty slots with bots when the room is short. */
  readonly fillWithBots: boolean;
  readonly label: string;
}

// ---------------------------------------------------------------------------
// Transport — the seam WebRTC would replace WebSocket at
// ---------------------------------------------------------------------------

export interface Transport {
  send(data: ArrayBuffer): void;
  /** Called for every inbound frame, in arrival order. */
  onMessage(handler: (data: ArrayBuffer) => void): void;
  onClose(handler: (reason: string) => void): void;
  close(reason?: string): void;
  /** Best-effort round trip, ms. */
  readonly rttMs: number;
}

// ---------------------------------------------------------------------------
// Client -> server
// ---------------------------------------------------------------------------

export const enum ClientMessage {
  Hello = 1,
  /** A run of inputs. Always carries redundancy — see `InputPacket`. */
  Input = 2,
  JoinRoom = 3,
  LeaveRoom = 4,
  Ready = 5,
  Chat = 6,
  Pong = 7,
}

export interface HelloPayload {
  protocolVersion: number;
  displayName: string;
  /** Preferred car body; the server may override for a slot. */
  car: CarBody;
  /**
   * Bearer token from /api/login. A server with accounts enabled requires it,
   * kicks `auth` without it, and takes the display name and ownership checks
   * from the DATABASE rather than from this payload. Optional in the type so
   * an account-less server (tests, benchmarks) still speaks the protocol.
   */
  authToken?: string;
}

/**
 * Inputs are tiny and loss-sensitive, so every packet repeats the last
 * `INPUT_REDUNDANCY` frames. One lost packet then costs nothing, which matters
 * far more than the handful of bytes: a dropped jump is a missed save.
 */
export interface InputPacket {
  /** Sequence of the NEWEST input in `inputs`. */
  seq: number;
  /** Client tick the newest input belongs to. */
  tick: number;
  /** Newest last. Length <= INPUT_REDUNDANCY. */
  inputs: readonly CarInput[];
}

export const INPUT_REDUNDANCY = 6;

// ---------------------------------------------------------------------------
// Server -> client
// ---------------------------------------------------------------------------

export const enum ServerMessage {
  Welcome = 128,
  RoomState = 129,
  Snapshot = 130,
  Event = 131,
  Ping = 132,
  Kick = 133,
}

export interface WelcomePayload {
  protocolVersion: number;
  /** Stable id for this connection. */
  playerId: number;
  /** Index into `World.cars` this client drives, or null while spectating. */
  carIndex: number | null;
  serverTickRate: number;
  snapshotRate: number;
}

/**
 * A full or delta-encoded world state.
 *
 * `baseTick` is what makes the transport swappable: a snapshot that deltas
 * against a tick the client no longer has is simply dropped and the client
 * asks for nothing — the next full snapshot repairs it. Under TCP that never
 * happens; under an unreliable transport it happens constantly and must be
 * survivable rather than fatal.
 */
export interface Snapshot {
  tick: number;
  /** Tick this delta is against, or -1 for a keyframe. */
  baseTick: number;
  /** Per-client: the newest input sequence the server has folded in. This is
   *  what the client rewinds to and replays from. */
  ackedInputSeq: number;
  cars: readonly CarSnapshot[];
  ball: BodySnapshot;
  /** Only the pads whose state changed since `baseTick`. */
  padChanges: readonly { index: number; active: boolean; respawnTimer: number }[];
  match: MatchSnapshotWire;
}

export interface BodySnapshot {
  position: readonly [number, number, number];
  velocity: readonly [number, number, number];
  angularVelocity: readonly [number, number, number];
}

export interface CarSnapshot extends BodySnapshot {
  /** Smallest-three encoded; decoded to the orientation matrix client-side. */
  orientation: readonly [number, number, number, number];
  boost: number;
  /** Packed: onGround | supersonic | demolished | hasFlip | isDodging | jumped. */
  flags: number;
}

export interface MatchSnapshotWire {
  phase: number;
  clock: number;
  score: readonly [number, number];
}

/** Things a client must not have to infer by diffing snapshots. */
export type NetEvent =
  | { kind: 'goal'; team: 0 | 1; scorer: number | null; speed: number }
  | { kind: 'demolition'; attacker: number; victim: number }
  | { kind: 'bump'; attacker: number; victim: number }
  | { kind: 'ballTouch'; carIndex: number; speed: number }
  | { kind: 'boostPickup'; carIndex: number; padIndex: number }
  | { kind: 'kickoff' }
  | { kind: 'matchEnded'; score: readonly [number, number] };

// ---------------------------------------------------------------------------
// Rooms
// ---------------------------------------------------------------------------

export interface RoomPlayer {
  playerId: number;
  displayName: string;
  team: 0 | 1;
  carIndex: number | null;
  car: CarBody;
  ready: boolean;
  isBot: boolean;
  /** Round trip in ms, for the scoreboard. */
  ping: number;
}

export interface RoomState {
  code: string;
  mode: MultiplayerMode;
  teamSize: number;
  players: readonly RoomPlayer[];
  mutators: MutatorConfig;
  /** True once the match has started. */
  inProgress: boolean;
}

// ---------------------------------------------------------------------------
// Timing
// ---------------------------------------------------------------------------

/**
 * The client simulates AHEAD of the server by roughly half the round trip plus
 * a jitter margin, so its inputs arrive just before the server needs them.
 * Too small and inputs land late and get dropped; too large and the player
 * feels their own car lag the world. Adjusted continuously, never jumped —
 * a step change in the lead is a visible teleport.
 */
export const DEFAULT_JITTER_MS = 30;

/** Snapshots per second. 30 is plenty given the client predicts and
 *  interpolates; 60 doubles the bill for a correction nobody sees. */
export const DEFAULT_SNAPSHOT_RATE = 30;

/** Hard cap on how far a client may run ahead before the server stops
 *  accepting its inputs, in ticks. Bounds the rewind cost of a lagging or
 *  malicious client. */
export const MAX_CLIENT_LEAD_TICKS = 60;
